import { RefObject, useCallback, useEffect, useState } from 'react'

const XMOBILE = 480
const MOBILE = 576
const TABLET = 768

interface Size {
  width: number
  height: number
  isTablet: boolean
  isMobile: boolean
  isXMobile: boolean
}

function useElementSize(ref?: RefObject<HTMLElement>, delay = 100): Size {
  const [size, setSize] = useState<Size>({
    width: 0,
    height: 0,
    isTablet: false,
    isMobile: false,
    isXMobile: false,
  })

  const updateSize = useCallback(() => {
    if (ref) {
      if (!ref?.current) {
        return
      }

      const { width, height } = ref.current.getBoundingClientRect()
      setSize({
        width,
        height,
        isTablet: width <= TABLET,
        isMobile: width <= MOBILE,
        isXMobile: width <= XMOBILE,
      })
    } else {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
        isTablet: window.innerWidth <= TABLET,
        isMobile: window.innerWidth <= MOBILE,
        isXMobile: window.innerWidth <= XMOBILE,
      })
    }
  }, [ref?.current])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    const debouncedUpdateSize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(updateSize, delay)
    }

    window.addEventListener('resize', debouncedUpdateSize)
    updateSize() // Initial size measurement

    return () => {
      window.removeEventListener('resize', debouncedUpdateSize)
      clearTimeout(timeoutId)
    }
  }, [updateSize, delay])

  return size
}

export default useElementSize
